export type CsvDelimiter = "," | ";" | "\t";

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

const DELIMITERS: CsvDelimiter[] = [";", ",", "\t"];

/** Picks the delimiter that occurs most often, outside quotes, in the first line. */
function detectDelimiter(text: string): CsvDelimiter {
  const counts = new Map<CsvDelimiter, number>(DELIMITERS.map((d) => [d, 0]));
  let inQuotes = false;
  for (const char of text) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (!inQuotes && (char === "\n" || char === "\r")) {
      break;
    } else if (!inQuotes && counts.has(char as CsvDelimiter)) {
      const delimiter = char as CsvDelimiter;
      counts.set(delimiter, (counts.get(delimiter) ?? 0) + 1);
    }
  }
  let best: CsvDelimiter = ";";
  let bestCount = 0;
  for (const [delimiter, count] of counts) {
    if (count > bestCount) {
      best = delimiter;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Reads a quoted run starting just after its opening quote: `""` stands for
 * one quote, the next lone quote closes it. Returns the text and the index
 * after the closing quote (or the end of the input when it never closes).
 */
function readQuoted(
  text: string,
  start: number,
): { value: string; next: number } {
  let value = "";
  let i = start;
  while (i < text.length) {
    const char = text[i];
    if (char === '"') {
      if (text[i + 1] !== '"') {
        return { value, next: i + 1 };
      }
      value += '"';
      i += 2;
    } else {
      value += char;
      i++;
    }
  }
  return { value, next: i };
}

/** Splits CSV text into records of fields (RFC 4180, tolerant of LF or CRLF). */
function splitRecords(text: string, delimiter: CsvDelimiter): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";

  const endField = () => {
    record.push(field);
    field = "";
  };
  const endRecord = () => {
    endField();
    records.push(record);
    record = [];
  };

  let i = 0;
  while (i < text.length) {
    const char = text[i];
    if (char === '"') {
      const quoted = readQuoted(text, i + 1);
      field += quoted.value;
      i = quoted.next;
      continue;
    }
    if (char === delimiter) {
      endField();
    } else if (char === "\r" || char === "\n") {
      endRecord();
      if (char === "\r" && text[i + 1] === "\n") i++;
    } else {
      field += char;
    }
    i++;
  }
  if (field !== "" || record.length > 0) {
    endRecord();
  }

  return records.filter((r) => r.some((value) => value.trim() !== ""));
}

/**
 * Parses CSV text with a header row into one record per line, keyed by the
 * header text as written.
 *
 * Detects `;`, `,` or tab as the delimiter unless one is given, strips a
 * byte-order mark, and skips blank lines. Every value stays a string: the
 * caller decides what "370,00" means.
 */
export function parseCsv(
  text: string,
  options: { delimiter?: CsvDelimiter } = {},
): ParsedCsv {
  const source = text.startsWith("﻿") ? text.slice(1) : text;
  const delimiter = options.delimiter ?? detectDelimiter(source);
  const [headerRecord, ...records] = splitRecords(source, delimiter);
  if (!headerRecord) {
    return { headers: [], rows: [] };
  }

  const headers = headerRecord.map((header) => header.trim());
  const rows = records.map((values) => {
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      if (header) {
        row[header] = (values[index] ?? "").trim();
      }
    });
    return row;
  });
  return { headers, rows };
}
